import { Button, Callout, Card, Checkbox, Flex, Separator, Text, TextField } from '@radix-ui/themes'
import { AlertTriangleIcon, SearchIcon, SheetIcon } from 'lucide-react'
import { ChangeEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import { Form, Label } from 'radix-ui'
import { parse } from 'date-fns'

import { filterByPlate, handleExport } from '../../utils/margin-report-helpers'

import { MarginDataTableCard } from './MarginDataTableCard'
import { MarginBarChartCard } from './MarginBarChartCard'
import { MarginCallouts } from './MarginCallouts'

import { ScreenWrapper } from '@/modules/common/components/ScreenWrapper'
import { dateToday, formatDateToYYYY_MM_DD } from '@/utils/utils'
import { Rule, validateFields } from '@/utils/validateForm'
import useMarginFetcher from '@/hooks/useMarginFetcher'
import Margin from '@/interfaces/Margin'

interface ReportFilters {
  since: string
  until: string
}

const today = new Date()
const startDate = new Date(today.getFullYear(), today.getMonth(), 1)

export const MarginReportScreen = () => {
  const [formData, setFormData] = useState<ReportFilters>({
    since: formatDateToYYYY_MM_DD(startDate),
    until: dateToday(),
  })

  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [searchDisabled, setSearchDisabled] = useState<boolean>(true)
  const { errorMargin, fetchMargins, loading } = useMarginFetcher()
  const [withAdditionals, setWithAdditionals] = useState(true)
  const [margins, setMargins] = useState<Margin[]>([])
  const [plateQuery, setPlateQuery] = useState('')
  const filteredMargins = useMemo(() => filterByPlate(margins, plateQuery), [margins, plateQuery])
  const [filters, setFilters] = useState({
    until: new Date(),
    since: today,
  })

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = ({ target: { name, value } }) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    setSearchDisabled(false)
  }

  const handleSearch = useCallback(async () => {
    const fieldsToValidate = [
      { name: 'since', value: formData.since, rule: Rule.REQUIRED },
      { name: 'until', value: formData.until, rule: Rule.REQUIRED },
    ]

    const validationErrors = validateFields(fieldsToValidate)

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }

    setErrors({})

    const margins: Margin[] = await fetchMargins({
      ...formData,
      withAdditionals,
    })

    setSearchDisabled(margins?.length > 0)
    setMargins(margins ?? [])
    setFilters({
      since: parse(formData.since, 'yyyy-MM-dd', new Date()),
      until: parse(formData.until, 'yyyy-MM-dd', new Date()),
    })
  }, [formData.since, formData.until, withAdditionals, fetchMargins])

  useEffect(() => {
    if (formData.since && formData.until) {
      handleSearch()
    }
  }, []) // Only run once on mount

  useEffect(() => {
    if (formData.since && formData.until) {
      fetchMargins({
        since: formData.since,
        until: formData.until,
        withAdditionals,
      }).then((newMargins) => {
        setMargins(newMargins ?? [])
      })
    }
  }, [withAdditionals]) // Only run when withAdditionals changes

  return (
    <ScreenWrapper mt="2">
      {errorMargin && (
        <Callout.Root color="red">
          <Callout.Icon>
            <AlertTriangleIcon />
          </Callout.Icon>
          <Callout.Text>{errorMargin?.message}</Callout.Text>
        </Callout.Root>
      )}

      <Card>
        <Flex gap="2" justify="between" align="center">
          <Flex gap="2" align="center">
            <Form.Root asChild>
              <Flex gap="2" align="center">
                <Form.Field name="since" serverInvalid={!!errors?.since} asChild>
                  <Flex gap="2" align="center">
                    <Form.Label>Desde</Form.Label>
                    <Form.Control
                      className="rt-TextFieldRoot rt-r-size-2 rt-variant-surface rt-reset rt-TextFieldInput"
                      style={{ borderRadius: 'var(--radius-2)' }}
                      name="since"
                      asChild
                    >
                      <input
                        onChange={handleInputChange}
                        value={formData.since}
                        disabled={loading}
                        name="since"
                        type="date"
                      />
                    </Form.Control>
                  </Flex>
                </Form.Field>

                <Form.Field name="until" serverInvalid={!!errors?.until} asChild>
                  <Flex gap="2" align="center">
                    <Form.Label>Hasta</Form.Label>
                    <Form.Control
                      className="rt-TextFieldRoot rt-r-size-2 rt-variant-surface rt-reset rt-TextFieldInput"
                      style={{ borderRadius: 'var(--radius-2)' }}
                      name="until"
                      asChild
                    >
                      <input
                        onChange={handleInputChange}
                        value={formData.until}
                        disabled={loading}
                        name="until"
                        type="date"
                      />
                    </Form.Control>
                  </Flex>
                </Form.Field>

                <Button onClick={handleSearch} disabled={loading || searchDisabled} loading={loading}>
                  <SearchIcon />
                  <Text>Buscar</Text>
                </Button>
              </Flex>
            </Form.Root>

            <Separator orientation="vertical" />

            <Flex align="center" gap="2" asChild>
              <Label.Label>
                Incluir Adicionales
                <Checkbox
                  onCheckedChange={(checked) => setWithAdditionals(checked === 'indeterminate' ? false : checked)}
                  checked={withAdditionals}
                  name="withAdditionals"
                  disabled={loading}
                  color="blue"
                  size="3"
                />
              </Label.Label>
            </Flex>

            <Separator orientation="vertical" />

            <TextField.Root
              onChange={(event) => setPlateQuery(event.target.value)}
              placeholder="Filtrar por patente"
              aria-label="Filtrar por patente"
              value={plateQuery}
              disabled={loading}
            >
              <TextField.Slot>
                <SearchIcon size={16} />
              </TextField.Slot>
            </TextField.Root>
          </Flex>

          <Button
            onClick={() => handleExport(filteredMargins, withAdditionals)}
            disabled={loading || filteredMargins.length === 0}
            color="green"
          >
            <SheetIcon />
            <Text>Exportar</Text>
          </Button>
        </Flex>
      </Card>

      <MarginCallouts margins={filteredMargins} filters={filters} withAdditionals={withAdditionals} />

      <MarginBarChartCard margins={filteredMargins} />

      <MarginDataTableCard margins={filteredMargins} withAdditionals={withAdditionals} />
    </ScreenWrapper>
  )
}
